/* eslint-disable no-unsafe-optional-chaining */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import CardListHeading from "@insite/content-library/Components/CardListHeading";
import Button from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import GridContainer from "@insite/mobius/GridContainer";
import Icon from "@insite/mobius/Icon";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Radio from "@insite/mobius/Radio";
import RadioGroup from "@insite/mobius/RadioGroup";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography/Typography";
import Block from "@store/Components/Common/Block";
import styles from "@store/Components/Store/StoreRetailModeSettingsView/styles";
import GridItem from "@store/MobiusOverrides/GridItem/GridItem";
import loadRetailModeSettings from "@store/Store/Data/RetailModeSettings/Handlers/LoadRetailModeSettings";
import patchRetailModeSettings from "@store/Store/Data/RetailModeSettings/Handlers/PatchRetailModeSettings";
import updateRetailModeSettings from "@store/Store/Data/RetailModeSettings/Handlers/UpdateRetailModeSettings";
import { emptyTierPricing } from "@store/Store/Data/RetailModeSettings/RetailModeSettingsSelector";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { PriceIncreaseType } from "@store/Types/RetailModeSettings";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const minNumberOfRows = 1;
const maxNumberOfRows = 10;

const mapStateToProps = (state: StoreApplicationState) => {
    const stateContext = state.store.data.retailModeSettings;

    return {
        isLoading: stateContext.isLoading,
        settings: stateContext.editingRetailMode,
    };
};

const mapDispatchToProps = {
    loadRetailModeSettings,
    patchRetailModeSettings,
    updateRetailModeSettings,
};

interface PricingTierValid {
    id: number;
    from: boolean;
    to: boolean;
    percent: boolean;
}

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const RetailModeSettingsView: React.FC<Props> = ({
    loadRetailModeSettings,
    patchRetailModeSettings,
    updateRetailModeSettings,
    settings,
    isLoading,
}) => {
    const [isDataNotValidOrNotModified, setIsDataNotValidOrNotModified] = useState<boolean | undefined>(true);
    const [globalMarginMarkupValid, setGlobalMarginMarkupValid] = useState<boolean | undefined>(true);
    const [pricingTiersValid, setPricingTiersValid] = useState<PricingTierValid[]>();
    const toasterContext = React.useContext(ToasterContext);

    const validateSettings = () => {
        let valid = settings !== null;

        if (settings?.activated) {
            const marginMarkupValid = verifyMarginMarkup(Number(settings?.priceIncreasePercent));
            valid = valid && marginMarkupValid;
            setGlobalMarginMarkupValid(marginMarkupValid);

            if (settings?.tierPricingActivated) {
                valid = valid && verifyPricingTiers();
            }
        }

        setIsDataNotValidOrNotModified(!valid);
    };

    const verifyPricingTiers = () => {
        const _pricingTiersValid = settings?.pricingTiers.map(
            x =>
            ({
                id: x.id,
                from: Number(x.from) >= 0 && Number(x.from) < Number(x.to),
                to: Number(x.to) > Number(x.from),
                percent: verifyMarginMarkup(Number(x.priceIncreasePercent)),
            } as PricingTierValid),
        );

        setPricingTiersValid(_pricingTiersValid);
        return _pricingTiersValid?.every(x => x.from && x.to && x.percent) ?? false;
    };

    const verifyMarginMarkup = (marginMarkup: number) => {
        return marginMarkup >= 0 && (settings?.priceIncreaseType !== PriceIncreaseType.Margin || marginMarkup < 100);
    };

    useEffect(() => {
        loadRetailModeSettings({ force: true });
    }, []);

    useEffect(() => {
        validateSettings();
    }, [settings]);

    const updateSettingsHandler = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        if (!isDataNotValidOrNotModified) {
            toasterContext.addToast({ body: siteMessage("Settings saved with success"), messageType: "success" });
            patchRetailModeSettings();
        }
    };

    const percentChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateRetailModeSettings({ priceIncreasePercent: event.target.value });
    };

    const activateSettingChangeHandler: CheckboxProps["onChange"] = (_, activated) => {
        updateRetailModeSettings({ activated });
    };

    const activateTierPricingSettingChangeHandler: CheckboxProps["onChange"] = (_, tierPricingActivated) => {
        updateRetailModeSettings({ tierPricingActivated });
    };

    const addRow = () => {
        let id = 0;
        if (settings && settings?.pricingTiers.length) {
            id = (settings.pricingTiers.reduce((prev, current) => (prev.id > current.id ? prev : current)).id || 0) + 1;
        }
        const pricingTiers = settings?.pricingTiers ? [...settings?.pricingTiers] : [];

        if (settings && settings?.pricingTiers.length >= maxNumberOfRows) {
            return;
        }

        pricingTiers.push({ ...emptyTierPricing, id });
        updateRetailModeSettings({ pricingTiers });
    };

    const removeRow = (idRow: number) => {
        if (settings && settings?.pricingTiers.length <= minNumberOfRows) {
            return;
        }
        const pricingTiers = settings?.pricingTiers.filter(row => row.id !== idRow);
        updateRetailModeSettings({ pricingTiers });
    };

    const updatedTierPricingsValue = (value: string, key: string, id: number) => {
        const pricingTiers = settings?.pricingTiers ? [...settings?.pricingTiers] : [];
        pricingTiers.find((element, index) => {
            if (id === element.id) {
                pricingTiers[index] = { ...pricingTiers[index], [key]: value };
                return true;
            }
            return false;
        });
        return pricingTiers;
    };

    const onGlobalMarginMarkupChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const priceIncreaseType = event.target.value;
        updateRetailModeSettings({ priceIncreaseType });
    };

    const onTierPricingInputChangeHandler = (event: React.ChangeEvent<HTMLInputElement>, key: string, id: number) => {
        const pricingTiers = updatedTierPricingsValue(event.target.value, key, id);
        updateRetailModeSettings({ ...settings, pricingTiers });
    };

    return (
        <LoadingOverlay loading={isLoading}>
            <StyledWrapper {...styles.wrapper}>
                <Block {...styles.retailModeSettingsBlock}>
                    <CardListHeading heading={translate("Global Settings")} />
                    <StyledWrapper {...styles.activateRetailPriceInputWrapper}>
                        <Checkbox onChange={activateSettingChangeHandler} checked={settings?.activated}>
                            {translate("Activate retail pricing")}
                        </Checkbox>
                    </StyledWrapper>
                    <StyledWrapper {...styles.inputWrapper}>
                        <Typography {...styles.inputLabel}>{translate("Margin/Markup")}</Typography>
                        <RadioGroup
                            {...styles.radioGroup}
                            value={settings?.priceIncreaseType}
                            onChangeHandler={onGlobalMarginMarkupChange}
                        >
                            {Object.values(PriceIncreaseType).map(value => (
                                <Radio key={value} value={value} disabled={!settings?.activated}>
                                    {/* eslint-disable-next-line spire/avoid-dynamic-translate */}
                                    {translate(value)}
                                </Radio>
                            ))}
                        </RadioGroup>
                    </StyledWrapper>
                    <StyledWrapper {...styles.inputWrapper}>
                        <TextField
                            type="number"
                            label={`${translate("Margin/Markup")} (%)`}
                            onChange={percentChangeHandler}
                            value={settings?.priceIncreasePercent}
                            error={!globalMarginMarkupValid}
                            disabled={!settings?.activated}
                            {...styles.increasePercentInputWrapper}
                        />
                    </StyledWrapper>
                </Block>
                <Block {...styles.retailModeSettingsBlock}>
                    <CardListHeading heading={translate("Tier Pricing Settings")} />
                    <StyledWrapper {...styles.inputWrapper}>
                        <Checkbox
                            onChange={activateTierPricingSettingChangeHandler}
                            checked={settings?.tierPricingActivated}
                            disabled={!settings?.activated}
                        >
                            {translate("Activate tier pricing")}
                        </Checkbox>
                    </StyledWrapper>
                </Block>
                <Block {...styles.retailModeSettingsBlock}>
                    <StyledWrapper {...styles.container}>
                        <GridContainer {...styles.tableGridHead}>
                            <GridItem {...styles.fromHeaderGridItem}>
                                <Typography {...styles.headerText}>{translate("From")}</Typography>
                            </GridItem>
                            <GridItem {...styles.toHeaderGridItem}>
                                <Typography {...styles.headerText}>{translate("To")}</Typography>
                            </GridItem>
                            <GridItem {...styles.marginHeaderGridItem}>
                                <Typography {...styles.headerText}>{translate("Margin/Markup")}</Typography>
                            </GridItem>
                        </GridContainer>
                        <GridContainer {...styles.tableGridBody}>
                            {settings &&
                                settings?.pricingTiers.length > 0 &&
                                settings?.pricingTiers.map((row, index: number) => {
                                    const validation = pricingTiersValid?.find(x => x.id === row.id);
                                    return (
                                        <><GridItem {...styles.tableGridRow} key={row.id}>
                                            <GridContainer {...styles.tableGridRowContainer} >
                                                <GridItem {...styles.mobileFromLabelGridItem}>
                                                    <Typography>
                                                        {translate("From")}
                                                    </Typography>
                                                </GridItem>
                                                <GridItem {...styles.fromInputGridItem}>                                                    
                                                    <TextField
                                                        type="number"
                                                        {...styles.tierPricingTextField}
                                                        onChange={event => onTierPricingInputChangeHandler(event, "from", row.id)}
                                                        value={row.from.toString()}
                                                        error={!validation?.from ?? false}
                                                        disabled={!settings?.activated || !settings?.tierPricingActivated} />
                                                    <Typography {...styles.firstSignTypography}>$</Typography>
                                                </GridItem>
                                                <GridItem {...styles.mobileRemoveButtonGridCell}>
                                                    <Button
                                                        {...styles.mobileRemoveButton}
                                                        onClick={() => removeRow(row.id)}
                                                        disabled={!settings?.activated ||
                                                            !settings?.tierPricingActivated ||
                                                            settings?.pricingTiers.length <= minNumberOfRows}
                                                    >
                                                        <Typography>
                                                            {translate("Delete")}
                                                        </Typography>
                                                        <Icon {...styles.mobileRemoveIcon} />
                                                    </Button>
                                                </GridItem>
                                                <GridItem {...styles.mobileFromLabelGridItem}>
                                                    <Typography>
                                                        {translate("To")}
                                                    </Typography>
                                                </GridItem>
                                                <GridItem {...styles.toInputGridItem}>
                                                    <TextField
                                                        type="number"
                                                        {...styles.tierPricingTextField}
                                                        onChange={event => onTierPricingInputChangeHandler(event, "to", row.id)}
                                                        value={row.to.toString()}
                                                        error={!validation?.to ?? false}
                                                        required
                                                        disabled={!settings?.activated || !settings?.tierPricingActivated} />
                                                    <Typography {...styles.signTypography}>$</Typography>
                                                </GridItem>
                                                <GridItem {...styles.mobileMarginLabelGridItem}>
                                                    <Typography>
                                                        {translate("Margin/Markup (%)")}
                                                    </Typography>
                                                </GridItem>
                                                <GridItem {...styles.marginInputGridItem}>
                                                    <TextField
                                                        type="number"
                                                        {...styles.mobileMarginTierPricingTextField}
                                                        onChange={event => onTierPricingInputChangeHandler(
                                                            event,
                                                            "priceIncreasePercent",
                                                            row.id
                                                        )}
                                                        value={row.priceIncreasePercent.toString()}
                                                        error={!validation?.percent ?? false}
                                                        required
                                                        disabled={!settings?.activated || !settings?.tierPricingActivated} />
                                                    <Typography {...styles.signTypography}>%</Typography>
                                                </GridItem>
                                                <GridItem {...styles.removeButtonGridCell}>
                                                    <Button
                                                        {...styles.removeButton}
                                                        onClick={() => removeRow(row.id)}
                                                        disabled={!settings?.activated ||
                                                            !settings?.tierPricingActivated ||
                                                            settings?.pricingTiers.length <= minNumberOfRows}
                                                    >
                                                        <Icon {...styles.removeIcon} />
                                                    </Button>
                                                </GridItem>
                                            </GridContainer>
                                        </GridItem>
                                        </>
                                    );
                                })}
                        </GridContainer>
                    </StyledWrapper>
                    <StyledWrapper {...styles.addRowWrapper}>
                        <Button
                            {...styles.addRowButton}
                            onClick={addRow}
                            disabled={
                                !settings?.activated ||
                                !settings?.tierPricingActivated ||
                                settings?.pricingTiers.length >= maxNumberOfRows
                            }
                        >
                            {translate("Insert Row")}
                        </Button>
                    </StyledWrapper>
                </Block>
                <Block {...styles.retailModeSettingsSavingButtonBlock}>
                    <Button
                        onClick={updateSettingsHandler}
                        disabled={isDataNotValidOrNotModified}
                        {...styles.saveButton}
                    >
                        {translate("Save All Settings")}
                    </Button>
                </Block>
            </StyledWrapper>
        </LoadingOverlay>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(RetailModeSettingsView);
